import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ProjectCardPreview from '../ui/ProjectCardPreview';
import {
  portfolioFilters,
  filterPortfolioProjects,
  liveProjectsCount,
} from '../../data/projects';

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.9); }
`;

const Section = styled.section`
  position: relative;
  padding: clamp(88px, 12vw, 128px) 0 clamp(96px, 12vw, 120px);
  overflow: hidden;
  background: radial-gradient(ellipse 80% 50% at 50% 0%, rgba(109, 187, 26, 0.07) 0%, transparent 55%),
    radial-gradient(ellipse 50% 40% at 100% 80%, rgba(14, 107, 60, 0.05) 0%, transparent 50%),
    #ffffff;
`;

const Bg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  .grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(14, 107, 60, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(14, 107, 60, 0.04) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse 90% 70% at 50% 30%, black 0%, transparent 75%);
  }

  .orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    &.a {
      width: 400px;
      height: 400px;
      top: -10%;
      right: -5%;
      background: rgba(109, 187, 26, 0.18);
    }
    &.b {
      width: 320px;
      height: 320px;
      bottom: 5%;
      left: -8%;
      background: rgba(14, 107, 60, 0.12);
    }
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 var(--container-padding, 24px);
`;

const Header = styled(motion.header)`
  max-width: 640px;
  margin-bottom: clamp(40px, 6vw, 56px);

  @media (max-width: 900px) {
    max-width: none;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 12px;
  margin-bottom: 20px;
  border-radius: 100px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(240, 250, 244, 0.95));
  border: 1px solid rgba(14, 107, 60, 0.14);
  box-shadow: 0 4px 20px rgba(14, 107, 60, 0.08);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #0e6b3c;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 10px rgba(34, 197, 94, 0.8);
    animation: ${pulse} 2s ease infinite;
  }
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(2.1rem, 5vw, 3.25rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.1;
  color: #07111f;
  margin: 0 0 14px;

  span {
    background: linear-gradient(135deg, #0e6b3c 0%, #6dbb1a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Sub = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.75;
  color: #64748b;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const FilterChip = styled.button`
  padding: 10px 18px;
  border-radius: 100px;
  border: 1px solid ${(p) => (p.$active ? '#0e6b3c' : '#e2e8f0')};
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, #0e6b3c, #0a5a32)' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#64748b')};
  font-weight: 700;
  font-size: 0.8rem;
  font-family: var(--font-display);
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? '0 8px 24px rgba(14, 107, 60, 0.28)' : '0 2px 8px rgba(15,23,42,0.04)')};
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-1px);
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 300px) 1fr;
  gap: clamp(24px, 4vw, 40px);
  align-items: start;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Nav = styled.nav`
  position: sticky;
  top: calc(var(--nav-height, 72px) + 20px);
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: calc(100vh - var(--nav-height, 72px) - 48px);
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 4px;
  }
`;

const NavItem = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 16px;
  text-align: left;
  cursor: pointer;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, #f0faf4, #fff)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(14, 107, 60, 0.25)' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 8px 28px rgba(14, 107, 60, 0.12)' : 'none')};
  transition: background 0.25s, border-color 0.25s, box-shadow 0.25s;

  &:hover {
    background: #f8fafc;
    border-color: #e8eef3;
  }
`;

const NavIcon = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, #0e6b3c, #6dbb1a)' : 'rgba(14, 107, 60, 0.08)')};
  color: ${(p) => (p.$active ? '#fff' : '#0e6b3c')};
  font-size: 1rem;
`;

const NavText = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-size: 0.72rem;
    color: #94a3b8;
    font-family: ui-monospace, monospace;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ActiveBar = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, #6dbb1a, #0e6b3c);
`;

const Stage = styled.div`
  position: relative;
`;

const NavArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.95);
  color: #0e6b3c;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  transition: transform 0.2s, background 0.2s;

  &:hover {
    background: #0e6b3c;
    color: #fff;
    transform: translateY(-50%) scale(1.05);
  }

  &.prev {
    left: -22px;
  }
  &.next {
    right: -22px;
  }

  @media (max-width: 1100px) {
    &.prev {
      left: 8px;
    }
    &.next {
      right: 8px;
    }
  }
`;

const Device = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  background: #0f172a;
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.08),
    0 40px 80px -20px rgba(14, 107, 60, 0.25),
    0 24px 48px rgba(15, 23, 42, 0.12);
`;

const DeviceBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  .dots {
    display: flex;
    gap: 6px;
    span {
      width: 11px;
      height: 11px;
      border-radius: 50%;
      &:nth-child(1) {
        background: #ff5f57;
      }
      &:nth-child(2) {
        background: #febc2e;
      }
      &:nth-child(3) {
        background: #28c840;
      }
    }
  }
`;

const UrlPill = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.75);
  font-family: ui-monospace, monospace;

  i {
    color: #4ade80;
    font-size: 0.65rem;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Screen = styled.div`
  position: relative;
  height: clamp(340px, 42vw, 480px);
  background: #fff;
  overflow: hidden;

  .preview-fill {
    position: absolute;
    inset: 0;
    height: 100%;
  }
`;

const ScreenOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 55%, rgba(7, 17, 31, 0.55) 100%);
  pointer-events: none;
  z-index: 2;
`;

const FloatCard = styled(motion.div)`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 24px;
  z-index: 3;
  padding: 22px 24px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`;

const FloatMeta = styled.div`
  flex: 1;
  min-width: 200px;

  .tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #0e6b3c;
    margin-bottom: 6px;
  }

  h3 {
    font-family: var(--font-display);
    font-size: clamp(1.2rem, 2.5vw, 1.5rem);
    font-weight: 800;
    color: #07111f;
    margin: 0 0 6px;
    line-height: 1.2;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.5;
    max-width: 420px;
  }
`;

const FloatActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 26px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0e6b3c, #0a5a32);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  font-family: var(--font-display);
  text-decoration: none;
  box-shadow: 0 12px 32px rgba(14, 107, 60, 0.35);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const SecondaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  font-weight: 600;
  font-size: 0.88rem;
  text-decoration: none;

  &:hover {
    border-color: #0e6b3c;
    color: #0e6b3c;
  }
`;

const TechRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  width: 100%;

  span {
    font-size: 0.68rem;
    padding: 4px 10px;
    border-radius: 100px;
    background: rgba(14, 107, 60, 0.08);
    color: #0e6b3c;
    font-weight: 600;
  }
`;

const MobileScroll = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding: 4px 0 20px;
    margin: 0 calc(-1 * var(--container-padding, 24px));
    padding-left: var(--container-padding, 24px);
    padding-right: var(--container-padding, 24px);
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MobileCard = styled.article`
  flex: 0 0 min(88vw, 360px);
  scroll-snap-align: center;
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8eef3;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.1);
`;

const MobileScreen = styled.div`
  height: 220px;
  overflow: hidden;
`;

const MobileBody = styled.div`
  padding: 18px;

  h3 {
    font-family: var(--font-display);
    font-size: 1.05rem;
    font-weight: 800;
    margin: 0 0 8px;
    color: #07111f;
  }

  p {
    font-size: 0.8rem;
    color: #64748b;
    margin: 0 0 14px;
    line-height: 1.5;
  }
`;

const MobileBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  background: #0e6b3c;
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin-top: 48px;
`;

const PageLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 32px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0e6b3c, #0a5a32);
  color: #fff;
  font-weight: 700;
  font-family: var(--font-display);
  text-decoration: none;
  box-shadow: 0 14px 36px rgba(14, 107, 60, 0.3);
`;

const slide = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.01 },
};

const PortfolioSpotlight = ({
  sectionId = 'portfolio',
  showPageCta = true,
  compactHeader = false,
}) => {
  const [filter, setFilter] = useState('All');
  const [activeId, setActiveId] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const projects = filterPortfolioProjects(filter);
  const active = projects.find((p) => p.id === activeId) || projects[0];

  useEffect(() => {
    if (projects.length && !projects.find((p) => p.id === activeId)) {
      setActiveId(projects[0].id);
    }
  }, [filter, projects, activeId]);

  const select = useCallback((id) => setActiveId(id), []);

  const go = useCallback(
    (dir) => {
      if (!projects.length || !active) return;
      const idx = projects.findIndex((p) => p.id === active.id);
      const next = (idx + dir + projects.length) % projects.length;
      setActiveId(projects[next].id);
    },
    [projects, active]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const liveUrl = active?.links?.live;
  const hasLive = liveUrl && liveUrl !== '#';

  return (
    <Section ref={ref} id={sectionId}>
      <Bg>
        <div className="grid" />
        <div className="orb a" />
        <div className="orb b" />
      </Bg>

      <Inner>
        {!compactHeader && (
          <Header
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55 }}
          >
            <Badge>
              <span className="dot" />
              {liveProjectsCount} live projects
            </Badge>
            <Title>
              All Live <span>Websites</span>
            </Title>
            <Sub>
              Pick any project to preview — every build is deployed with a real URL you can open
              instantly.
            </Sub>
          </Header>
        )}

        <Filters>
          {portfolioFilters.map((c) => (
            <FilterChip
              key={c}
              type="button"
              $active={filter === c}
              onClick={() => setFilter(c)}
            >
              {c}
              <span style={{ opacity: 0.75, marginLeft: 4 }}>
                ({filterPortfolioProjects(c).length})
              </span>
            </FilterChip>
          ))}
        </Filters>

        {projects.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#94a3b8', padding: 48 }}>No projects in this category.</p>
        ) : (
          <>
            <Layout>
              <Nav aria-label="Portfolio projects">
                {projects.map((p) => {
                  const isActive = active?.id === p.id;
                  return (
                    <NavItem
                      key={p.id}
                      type="button"
                      $active={isActive}
                      onClick={() => select(p.id)}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      {isActive && (
                        <ActiveBar layoutId="portfolio-active-bar" transition={{ type: 'spring', stiffness: 500, damping: 35 }} />
                      )}
                      <NavIcon $active={isActive}>
                        <i className={p.icon || 'fas fa-globe'} />
                      </NavIcon>
                      <NavText>
                        <strong>{p.title}</strong>
                        <span>{p.domain}</span>
                      </NavText>
                      {p.isProduct && (
                        <span
                          style={{
                            fontSize: '0.6rem',
                            fontWeight: 800,
                            padding: '3px 8px',
                            borderRadius: 100,
                            background: '#f2b705',
                            color: '#07111f',
                          }}
                        >
                          Product
                        </span>
                      )}
                    </NavItem>
                  );
                })}
              </Nav>

              <Stage>
                {projects.length > 1 && (
                  <>
                    <NavArrow type="button" className="prev" onClick={() => go(-1)} aria-label="Previous project">
                      <i className="fas fa-chevron-left" />
                    </NavArrow>
                    <NavArrow type="button" className="next" onClick={() => go(1)} aria-label="Next project">
                      <i className="fas fa-chevron-right" />
                    </NavArrow>
                  </>
                )}
                <AnimatePresence mode="wait">
                  {active && (
                    <Device key={active.id} {...slide} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
                      <DeviceBar>
                        <div className="dots">
                          <span /><span /><span />
                        </div>
                        <UrlPill>
                          <i className="fas fa-lock" />
                          <span>{active.domain}</span>
                        </UrlPill>
                      </DeviceBar>
                      <Screen>
                        <div className="preview-fill">
                          <ProjectCardPreview project={active} height="100%" frameless />
                        </div>
                        <ScreenOverlay />
                        <FloatCard
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.12, duration: 0.4 }}
                        >
                          <FloatMeta>
                            <div className="tag">
                              <i className={active.icon} /> {active.categoryLabel}
                            </div>
                            <h3>{active.title}</h3>
                            <p>{active.description}</p>
                            {active.tech?.length > 0 && (
                              <TechRow>
                                {active.tech.slice(0, 5).map((t) => (
                                  <span key={t}>{t}</span>
                                ))}
                              </TechRow>
                            )}
                          </FloatMeta>
                          <FloatActions>
                            {hasLive && (
                              <PrimaryBtn href={liveUrl} target="_blank" rel="noopener noreferrer">
                                Visit live site <i className="fas fa-external-link-alt" />
                              </PrimaryBtn>
                            )}
                            {active.detailPath && (
                              <SecondaryBtn to={active.detailPath}>
                                All features <i className="fas fa-arrow-right" />
                              </SecondaryBtn>
                            )}
                          </FloatActions>
                        </FloatCard>
                      </Screen>
                    </Device>
                  )}
                </AnimatePresence>
              </Stage>
            </Layout>

            <MobileScroll>
              {projects.map((p) => (
                <MobileCard key={p.id}>
                  <MobileScreen>
                    <ProjectCardPreview project={p} height="220px" frameless />
                  </MobileScreen>
                  <MobileBody>
                    <h3>{p.title}</h3>
                    <p>{p.platform}</p>
                    {p.links?.live && p.links.live !== '#' && (
                      <MobileBtn href={p.links.live} target="_blank" rel="noopener noreferrer">
                        Visit {p.domain} <i className="fas fa-external-link-alt" />
                      </MobileBtn>
                    )}
                  </MobileBody>
                </MobileCard>
              ))}
            </MobileScroll>
          </>
        )}

        {showPageCta && (
          <CtaRow>
            <PageLink to="/projects">
              Full portfolio page <i className="fas fa-arrow-right" />
            </PageLink>
            <PageLink
              to="/contact"
              style={{
                background: '#fff',
                color: '#0e6b3c',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
              }}
            >
              Start a project
            </PageLink>
          </CtaRow>
        )}
      </Inner>
    </Section>
  );
};

export default PortfolioSpotlight;
